import { AvatarUpload } from "@/layouts/signed-in/Avatar/AvatarUpload";
import { auth } from "@/services/auth-service";
import { selfUpdateUserComprehensive } from "@/services/user-service";
import { useUserStore } from "@/stores";
import { IUser, UserAccessLevel } from "@/stores/user-store";
import {
	CheckOutlined,
	CloseOutlined,
	EditOutlined,
	LockOutlined,
} from "@ant-design/icons";
import { Button, Card, Input, Select, Spin, Typography, message } from "antd";
import { updateProfile } from "firebase/auth";
import type { FC } from "react";
import { useReducer } from "react";

const { Title, Text } = Typography;
const { Option } = Select;

interface ProfileState {
	nameInput: {
		value: string;
		isEditing: boolean;
	};
	isLoading: boolean;
}

type ProfileAction =
	| { type: "SET_NAME_INPUT"; payload: string }
	| { type: "TOGGLE_NAME_EDIT" }
	| { type: "SET_LOADING"; payload: boolean };

const initialState: ProfileState = {
	nameInput: {
		value: "",
		isEditing: false,
	},
	isLoading: false,
};

const profileReducer = (
	state: ProfileState,
	action: ProfileAction,
): ProfileState => {
	switch (action.type) {
		case "SET_NAME_INPUT":
			return {
				...state,
				nameInput: {
					...state.nameInput,
					value: action.payload,
				},
			};
		case "TOGGLE_NAME_EDIT":
			return {
				...state,
				nameInput: {
					...state.nameInput,
					isEditing: !state.nameInput.isEditing,
				},
			};
		case "SET_LOADING":
			return {
				...state,
				isLoading: action.payload,
			};
		default:
			return state;
	}
};

const Profile: FC = () => {
	const { user, setUser } = useUserStore();

	const [state, dispatch] = useReducer(profileReducer, {
		...initialState,
		nameInput: {
			value: auth?.currentUser?.displayName || "",
			isEditing: false,
		},
	});

	const handleUpdateUsername = async () => {
		if (
			!auth.currentUser ||
			!state.nameInput.value ||
			state.nameInput.value === auth.currentUser.displayName ||
			!user
		) {
			return;
		}

		const previousUser = user;
		const updatedUser: IUser = {
			...user,
			name: state.nameInput.value,
		};

		try {
			dispatch({ type: "SET_LOADING", payload: true });

			// Update Firebase profile first
			await updateProfile(auth.currentUser, {
				displayName: state.nameInput.value,
			});

			// Sync with backend and get complete user data
			const response = await selfUpdateUserComprehensive(updatedUser);
			setUser(response);

			message.success("Username successfully updated");
			dispatch({ type: "TOGGLE_NAME_EDIT" });
		} catch (error: any) {
			// Rollback on failure
			setUser(previousUser);
			message.error(error.message || "Failed to update username");
		} finally {
			dispatch({ type: "SET_LOADING", payload: false });
		}
	};

	const handleOrganizationChange = async (value: string) => {
		if (!user) return;

		const previousUser = user;
		const allowedOrgs =
			user.allowed_organizations || user.allowedOrganizations || [];
		const selectedOrg = allowedOrgs.find(
			(org) => (org.external_firebase_id || org.externalFirebaseId) === value,
		);

		if (!selectedOrg) return;

		const oldOrgId = user.organization?.id || user.organization_id;

		try {
			dispatch({ type: "SET_LOADING", payload: true });

			// STEP 1: Reset all organization-specific state BEFORE switching
			const { resetOrganizationState } = await import(
				"@/utils/org-switch-manager"
			);
			await resetOrganizationState(oldOrgId, selectedOrg.id);

			// STEP 2: Sync with backend and get complete user data with new org details
			const response = await selfUpdateUserComprehensive({
				...user,
				organization_id: selectedOrg.id,
			});

			// STEP 3: Update user in store
			setUser(response);

			message.success("Organization has been changed");

			// STEP 4: Optional - force page reload for guaranteed clean slate
			// Uncomment if you still see stale data issues:
			// setTimeout(() => window.location.reload(), 100);
		} catch (error: any) {
			console.error("Failed to switch organization:", error);
			// Rollback on failure
			setUser(previousUser);
			message.error(error.message || "Failed to update organization");
		} finally {
			dispatch({ type: "SET_LOADING", payload: false });
		}
	};

	const renderNameInput = () => (
		<div className="flex items-center gap-2 w-full">
			{state.nameInput.isEditing ? (
				<>
					<Input
						value={state.nameInput.value}
						onChange={(e) =>
							dispatch({ type: "SET_NAME_INPUT", payload: e.target.value })
						}
						className="flex-1"
					/>
					<Button
						type="text"
						icon={<CheckOutlined />}
						onClick={handleUpdateUsername}
					/>
					<Button
						type="text"
						icon={<CloseOutlined />}
						onClick={() => dispatch({ type: "TOGGLE_NAME_EDIT" })}
					/>
				</>
			) : (
				<>
					<Text className="flex-1">{user?.name}</Text>
					<Button
						type="text"
						icon={<EditOutlined />}
						onClick={() => dispatch({ type: "TOGGLE_NAME_EDIT" })}
					/>
				</>
			)}
		</div>
	);

	if (!user) {
		return (
			<div className="flex justify-center items-center h-full">
				<Spin size="large" />
			</div>
		);
	}

	return (
		<div className="max-w-2xl mx-auto p-6">
			<Title level={4} className="mb-2 text-center">
				Profile
			</Title>
			<Text className="text-gray-500 block mb-6 text-center">
				Manage your personal information and photo here.
			</Text>

			<Card className="shadow-sm bg-white rounded-lg">
				<div className="flex flex-col items-center mb-8">
					<div className="w-24 h-24 mb-4">
						<AvatarUpload />
					</div>
					<Title level={5} className="m-0">
						{user.name}
					</Title>
				</div>

				<div className="space-y-6 max-w-md mx-auto">
					<div>
						<Text className="text-gray-500 block mb-2">Preferred Name</Text>
						{renderNameInput()}
					</div>

					<div>
						<Text className="text-gray-500 block mb-2">Email</Text>
						<Input disabled value={user.email} />
					</div>

					<div>
						<Text className="text-gray-500 block mb-2">Password</Text>
						<Input.Password
							disabled
							value="********"
							prefix={<LockOutlined />}
						/>
					</div>

					<div>
						<Text className="text-gray-500 block mb-2">Organization</Text>
						<Select
							className="w-full"
							value={
								user.organization.external_firebase_id ||
								user.organization.externalFirebaseId
							}
							onChange={handleOrganizationChange}
							disabled={
								(user.role.access_level || user.role.accessLevel || 0) <
									UserAccessLevel.SUPER_ADMIN ||
								((user.allowed_organizations || user.allowedOrganizations)
									?.length ?? 0) <= 1 ||
								state.isLoading
							}
						>
							{(
								user.allowed_organizations ||
								user.allowedOrganizations ||
								[]
							).map((org) => (
								<Option
									key={org.id}
									value={org.external_firebase_id || org.externalFirebaseId}
								>
									{org.full_name || org.fullName}
								</Option>
							))}
						</Select>
						{(user.role.access_level || user.role.accessLevel || 0) <
							UserAccessLevel.SUPER_ADMIN && (
							<Text className="text-gray-400 text-xs mt-1 block">
								Only super admins can switch organizations
							</Text>
						)}
					</div>

					<div>
						<Text className="text-gray-500 block mb-2">Role</Text>
						<Input disabled value={user.role.name} />
					</div>
				</div>
			</Card>
		</div>
	);
};

export { Profile };
